<template>
	<div class="full-page-container ecommerce-product-page">
		<main
			id="content"
			class="page-container"
		>
			<div class="top-banner">
				<div class="content-container top-banner-inner">
					<ECommerceUSP class="usp" />
				</div>
			</div>
			<div
				class="breadcrumbs-spacer"
				v-if="metadata.disableBreadcrumbs"
			></div>
			<slot />
		</main>
	</div>
</template>

<script>
import ECommerceProductMixin from "~/components/ecommerce/ECommerceProductMixin/ECommerceProductMixin.js";
import dateHelper from "~/helpers/dateHelper.ts";
import safeUrl from "~/helpers/ecommerce/safeEcommerceUrl.js";
import { usePageStore } from "~/pinia/platform/page.ts";
import { mapState } from "pinia";

export default defineNuxtComponent({
	name: "ECommerceProductPage",
	mixins: [ECommerceProductMixin],
	scrollToTop: false,

	data() {
		return {
			popStateListener: null,
		};
	},

	computed: {
		...mapState(usePageStore, ["page"]),
		metadata() {
			return this.page.metadata;
		},
		variantUrls() {
			const path = useRoute().fullPath;

			return this.eCommerceProduct?.variants?.map((variant) => {
				return path.replace(/(.+?-)(\d+?)\//, `$1${variant.id}/`);
			});
		},
		schemaData() {
			let availability;

			if (!this.eCommerceProduct) return {};

			if (this.eCommerceProduct?.inventory?.[0]?.amount > 5) availability = "InStock";
			else if (this.eCommerceProduct?.inventory?.[0]?.amount > 0) availability = "LimitedAvailability";
			else availability = "OutOfStock";

			const url = `https://www.telenor.no${safeUrl(
				this.eCommerceProduct.category,
				this.eCommerceProduct.brand,
				this.eCommerceProduct.title,
				this.eCommerceProduct.condition,
			)}`;

			const productSchema = JSON.parse(
				JSON.stringify({
					"@context": "http://schema.org",
					"@type": "Product",
					color: this.eCommerceProduct.color,
					brand: {
						"@type": "Brand",
						name: this.eCommerceProduct.brand,
					},
					gtin: this.eCommerceProduct.gtin,
					name: this.eCommerceProduct.title,
					description: this.eCommerceProduct.description,
					category: this.eCommerceProduct.category,
					image: {
						"@type": "ImageObject",
						url: this.eCommerceProduct.imageLink,
					},
					sku: this.eCommerceProduct.id,
					offers: [
						{
							price: Math.ceil(
								parseFloat(
									this.checkMemberPrice(this.eCommerceProduct.memberPrice?.prices)
										? this.eCommerceProduct.memberPrice?.prices?.withVat?.memberPrice
										: this.eCommerceProduct.salePriceWithVat || this.eCommerceProduct.priceWithVat,
								),
							),
							priceCurrency: "NOK",
							availability,
							url,
						},
					],
					aggregateRating: {
						ratingValue: this.eCommerceProduct.rating,
						worstRating: 1,
						bestRating: 5,
						ratingCount: this.eCommerceProduct.reviewCount,
					},
				}),
			);

			if (this.eCommerceProduct.prices?.downPaymentProducts?.length > 0) {
				const downPaymentProduct = this.eCommerceProduct.prices.downPaymentProducts.reduce((max, item) =>
					item.monthlyInstallments > max.monthlyInstallments ? item : max,
				);

				productSchema.offers.push({
					priceSpecification: {
						billingDuration: downPaymentProduct.monthlyInstallments,
						unitCode: "MON",
						billingIncrement: 1,
						"@type": "UnitPriceSpecification",
					},
					price: downPaymentProduct.prices.totalPrice.value,
					priceCurrency: "NOK",
					availability,
					url,
				});
			}

			return productSchema;
		},
	},

	head() {
		let obj = {
			script: [
				{
					json: this.schemaData,
					type: "application/ld+json",
				},
			],
		};

		if (this.variantUrls) {
			obj = {
				...obj,
				link: this.variantUrls.map((variantUrl) => {
					return {
						rel: "prefetch",
						href: variantUrl,
					};
				}),
			};
		}

		obj.meta = [{ name: "description", content: this.eCommerceProduct?.descriptionSummary }];

		return obj;
	},

	async mounted() {
		this.popStateListener = () => {
			window.location.reload();
		};
		window.addEventListener("popstate", this.popStateListener);
	},

	beforeUnmount() {
		window.removeEventListener("popstate", this.popStateListener);
	},

	methods: {
		checkMemberPrice(memberPrice) {
			if (memberPrice?.prices) {
				const activeMemberPrice = dateHelper.todayIsBetween(
					new Date(memberPrice?.startDate),
					new Date(memberPrice?.endDate),
				);
				return activeMemberPrice && memberPrice?.prices ? true : false;
			}

			return false;
		},
	},
});
</script>

<style scoped lang="scss">
.breadcrumbs-spacer {
	height: $spacing-2xl;

	@include breakpoint(mobile) {
		height: 0;
	}
}

h1 {
	margin: 50px auto;

	@include font-title-xl;
}

.container {
	max-width: 1280px;
	margin: 0 auto;
}

:deep(.component) {
	+ .component {
		margin-top: $spacing-l !important;
	}
}

.layout-width-70 {
	width: 70%;
	margin: 0 auto;

	@include breakpoint(mobile) {
		width: 100%;
	}
}

.ingress,
.ingress p {
	@include font-text-xl;
}

.ingress {
	margin: 50px auto 0;
	max-width: 660px;
}

.byline {
	color: $color-neutrals-600-shade;
	border-top: 1px solid $color-neutrals-200-tint;
	padding-top: 15px;
	margin-top: 50px;
	display: flex;

	@include font-text-s;

	.col {
		padding: 0;
	}

	a {
		text-decoration: none;
		color: $color-cta-default;
	}
}

.top-banner {
	border-bottom: 1px solid $color-neutrals-200-tint;
	border-top: 1px solid $color-neutrals-200-tint;
	background-color: $color-primary-superlight;

	.top-banner-inner {
		display: flex;
		justify-content: center;
		align-items: center;

		.usp {
			margin: 16px auto;
		}
	}
}
</style>
